/**
 * Savetnik transcript — the two-hop read over the S10a bridge mirrors.
 *
 * crm_lead_inbox carries no chat_session_id column (disposition 8 of the
 * S10a plan): the contract is `source_ref = mktg_training_signups.id::text`.
 * This hook hops mirrored signup -> chat_session_id -> mirrored session,
 * entirely through RLS-gated org-scoped reads (no service-role endpoint,
 * no new HTTP surface — the mirrors are readable by `authenticated` under
 * `mktg_mirror_*_org_read`, migration 20260807230000).
 *
 * Query key is a flat ['savetnik-transcript', organizationId, sourceRef]:
 * the mirrors are ORG-scoped, not seat-scoped (S9a lesson — see
 * useCrmQueryScope's [userId, organizationId] tuple, used where RLS is
 * seat-aware; the Savetnik mirrors are not), so organizationId alone is a
 * sufficient cache key.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import type { Tables } from '@/types/database';

export type SavetnikSignupRow = Tables<'mktg_mirror_training_signups'>;
export type SavetnikSessionRow = Tables<'mktg_mirror_savetnik_sessions'>;

export interface SavetnikTranscript {
  signup: SavetnikSignupRow;
  /** null when the signup carries no chat_session_id, or the session row is absent from the mirror. */
  session: SavetnikSessionRow | null;
}

export const savetnikTranscriptKeys = {
  detail: (organizationId: string, sourceRef: string) =>
    ['savetnik-transcript', organizationId, sourceRef] as const,
};

export interface UseSavetnikTranscriptOptions {
  /** The drawer's own open state — the query never fires while it is closed. */
  enabled: boolean;
}

export function useSavetnikTranscript(
  sourceRef: string | null,
  options: UseSavetnikTranscriptOptions,
): UseQueryResult<SavetnikTranscript | null> {
  const { organizationId } = useAuth();

  return useQuery<SavetnikTranscript | null>({
    queryKey: savetnikTranscriptKeys.detail(
      organizationId ?? 'unresolved-organization',
      sourceRef ?? 'unresolved-source-ref',
    ),
    queryFn: async () => {
      const { data: signupData, error: signupError } = await supabase
        .from('mktg_mirror_training_signups')
        .select('*')
        .eq('organization_id', organizationId as string)
        .eq('id', sourceRef as string)
        .maybeSingle();

      if (signupError) throw signupError;
      if (!signupData) return null;

      const signup = signupData as SavetnikSignupRow;

      if (!signup.chat_session_id) {
        return { signup, session: null };
      }

      const { data: sessionData, error: sessionError } = await supabase
        .from('mktg_mirror_savetnik_sessions')
        .select('*')
        .eq('organization_id', organizationId as string)
        .eq('id', signup.chat_session_id)
        .maybeSingle();

      if (sessionError) throw sessionError;

      return { signup, session: (sessionData as SavetnikSessionRow | null) ?? null };
    },
    enabled: options.enabled && organizationId !== null && sourceRef !== null,
    staleTime: 30_000,
  });
}
